import React from 'react';

import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';

import Table from '../../components/Table/Table';

import { ButtonExport } from '../../forms/Buttons/ButtonsExport';
import { numberFormat } from '../../helper/MaskHelper';
import { isCoordenadorOuApoioSecretarial } from '../../security/AuthorizedFunction';

const COLUNAS_KPI = [
    { label: 'Recebidas', name: 'recebidas' },
    { label: 'Aguard. Digitalização', name: 'aguardandoDigitalizacao' },
    { label: 'Digitalizadas', name: 'digitalizadas' },
    { label: 'Cortadas', name: 'cortadas' },
    { label: 'Leitura', name: 'leitura' },
    { label: 'Na Fila de Digitação', name: 'emDigitacao' },
    { label: 'Digitadas', name: 'digitadas' },
    { label: 'Em Revisão pela OBMEP Central', name: 'emRevisao' },
    { label: 'Revisadas', name: 'revisadas' },
    { label: 'Inval. Pendentes', name: 'invalidadasPendentes' },
    { label: 'Com Problema', name: 'comProblema' },
    { label: 'Descartadas', name: 'descartadas' },
    { label: 'Finalizadas', name: 'finalizadas' },
];

const COLUNAS_KPI_COORD = [
    { label: 'Recebidas', name: 'recebidas' },
    { label: 'Aguard. Digitalização', name: 'aguardandoDigitalizacao' },
    { label: 'Digitalizadas', name: 'digitalizadas' },
    { label: 'Leitura', name: 'leitura' },
    { label: 'Na Fila de Digitação', name: 'emDigitacao' },
    { label: 'Digitadas', name: 'digitadas' },
    { label: 'Em Revisão pela OBMEP Central', name: 'emRevisao' },
    { label: 'Descartadas', name: 'descartadas' },
    { label: 'Finalizadas', name: 'finalizadas' },
];

const PESO_RECEBIMENTO = 20;
const PESO_DIGITALIZACAO = 20;
const PESO_DIGITACAO = 60;

const COR_VERDE = '#e8f5e9';
const COR_AMARELO = '#fff8e1';
const COR_VERMELHO = '#ffebee';

const CATEGORIA_COLUNA = {
    digitalizadas: 'concluida',
    cortadas: 'concluida',
    leitura: 'concluida',
    digitadas: 'concluida',
    revisadas: 'concluida',
    finalizadas: 'concluida',
    aguardandoDigitalizacao: 'bloqueante',
    emDigitacao: 'bloqueante',
    emRevisao: 'bloqueante',
    invalidadasPendentes: 'bloqueante',
    comProblema: 'problema',
};

const estiloDestaque = cor => ({ backgroundColor: cor, fontWeight: 700, padding: '2px 6px', borderRadius: 4 });

const formatarPercentual = valor =>
    `${valor.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

export const TabelaRegioes = ({ regioes, loading, podeExportar, onExport }) => {
    const coord = isCoordenadorOuApoioSecretarial();
    const kpis = coord ? COLUNAS_KPI_COORD : COLUNAS_KPI;
    const dados = regioes ?? [];

    const porRegiao = new Map(dados.map(r => [r.regiao, r]));

    const percentualConcluido = regiao => {
        const base = (regiao?.recebidas ?? 0) - (regiao?.descartadas ?? 0);

        if (base <= 0)
            return null;

        return ((regiao?.finalizadas ?? 0) / base) * 100;
    };

    const progressoRegional = regiao => {
        const base = (regiao?.recebidas ?? 0) - (regiao?.descartadas ?? 0);

        if (base <= 0)
            return 0;

        const razao = contagem => Math.min(Math.max(contagem / base, 0), 1);

        return Math.min(
            PESO_RECEBIMENTO * razao(regiao?.recebidas ?? 0)
                + PESO_DIGITALIZACAO * razao(regiao?.digitalizadas ?? 0)
                + PESO_DIGITACAO * razao(regiao?.digitadas ?? 0),
            100
        );
    };

    const corDoValor = (nome, valor, regiao) => {
        const numero = valor ?? 0;
        const categoria = CATEGORIA_COLUNA[nome];
        const base = regiao?.recebidas ?? 0;

        if (categoria === 'concluida') {
            if (base <= 0)
                return undefined;

            return numero === base ? COR_VERDE : COR_AMARELO;
        }

        if (categoria === 'problema')
            return numero > 0 ? COR_VERMELHO : undefined;

        if (categoria === 'bloqueante')
            return numero > 0 ? COR_AMARELO : undefined;

        return undefined;
    };

    const colunaPercentual = (label, name, calcular) => ({
        label,
        name,
        align: 'right',
        width: 12,
        func: (_, id) => {
            const valor = calcular(porRegiao.get(id));
            const cor = valor === null ? undefined : (valor >= 100 ? COR_VERDE : COR_AMARELO);

            return (
                <span style={ cor ? estiloDestaque(cor) : undefined }>
                    { valor === null ? '—' : formatarPercentual(valor) }
                </span>
            );
        },
    });

    const columns = [
        { label: 'Região', name: 'regiao', key: true, align: 'left', width: 10, orderLabel: 'asc' },
        ...kpis.map(c => ({
            label: c.label,
            name: c.name,
            align: 'right',
            func: (valor, id) => {
                const cor = corDoValor(c.name, valor, porRegiao.get(id));

                return (
                    <span style={ cor ? estiloDestaque(cor) : undefined }>
                        {numberFormat(valor ?? 0)}
                    </span>
                );
            },
        })),
        colunaPercentual('% Processo Regional', 'processo', progressoRegional),
        ...(coord ? [] : [ colunaPercentual('% Concluído', 'concluido', percentualConcluido) ]),
    ];

    return (
        <Grid item xs={12}>
            <Card>
                <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">Por Região</Typography>
                        { podeExportar && onExport &&
                            <ButtonExport noGrid title="Exportar Planilha" onClick={onExport} />
                        }
                    </Box>

                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ minWidth: 1080 }}>
                            <Table
                                columns={columns}
                                tableData={regioes ?? []}
                                actions={[]}
                                loading={loading}
                                page={null}
                                rowsPerPage={null}
                                sort={null}
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Grid>
    );
}

export default TabelaRegioes;
